import React, { Suspense, lazy } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import ThemeContextProvider from './theme/ThemeContext';
import { UserProvider } from './components/userContext';
import { ConfigProvider } from './components/configContext';
import { PermissionProvider } from './components/permissionContext';
import { VehicleTypesProvider } from './components/VehicleTypesProvider';
import { PrivateRoute } from './components/privateroute';
import ErrorPage from './pages/error-page';
import MainLayout from './MainLayout';
import ErrorBoundary from './components/errorBoundary';
import LazyLoadFallback from './components/LazyLoadFallback';

// ============================================
// LAZY LOADED COMPONENTS (Code-Splitting)
// ============================================

// --- Core / Public ---
const Dashboard = lazy(() => import('./components/dashboard'));
const RegistrationPage = lazy(() => import('./pages/registration'));
const ConfirmationPage = lazy(() => import('./pages/confirmationRegistration'));
const Login = lazy(() => import('./pages/login'));
const PasswordResetPage = lazy(() => import('./pages/resetPasswordPage'));
const ReleaseNotes = lazy(() => import('./pages/releaseNotes'));
const ErrorComponent = lazy(() => import('./pages/ErrorComponent'));
const AGB = lazy(() => import('./pages/agb'));
const TenantAGBView = lazy(() => import('./pages/TenantAGBView'));
const TenantAGBManagement = lazy(() => import('./pages/TenantAGBManagement'));
const Impressum = lazy(() => import('./pages/impressum'));
const Datenschutz = lazy(() => import('./pages/datenschutz'));

// --- User & Authenticated ---
const ChangePasswordPage = lazy(() => import('./pages/changePasswordPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const TrustedDevicesPage = lazy(() => import('./pages/TrustedDevicesPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ForcedTwoFactorSetupPage = lazy(() => import('./pages/ForcedTwoFactorSetupPage'));

// --- Orders / Transport ---
const TransportFormPage = lazy(() => import('./pages/transportFormPage'));
const KrankenfahrtFormPage = lazy(() => import('./pages/KrankenfahrtFormPage'));
const MedicalMaterialFormPage = lazy(() => import('./pages/MedicalMaterialFormPage'));
const TransportList = lazy(() => import('./pages/transportList'));
const HeadOfSeriesList = lazy(() => import('./pages/transportListForSeries'));

// --- Admin Data & Config ---
const UsersList = lazy(() => import('./pages/usersPageAdmin'));
const EditUserPage = lazy(() => import('./pages/editUserPage'));
const InstitutionsList = lazy(() => import('./pages/institutionsList'));
const InstitutionPage = lazy(() => import('./pages/institutionPage'));
const LogsPage = lazy(() => import('./pages/logsPage'));
const TimeSlotManagement = lazy(() => import('./pages/timeSlotManagement'));
const InfektionenList = lazy(() => import('./pages/infektionenList'));
const MobileAppMasterData = lazy(() => import('./pages/mobileAppMasterData'));
const ConfigTabs = lazy(() => import('./pages/configTabs'));
const MailTemplatesPage = lazy(() => import('./pages/MailTemplatesPage'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const AdminNavigatorPage = lazy(() => import('./pages/AdminNavigatorPage'));
const SystemReport = lazy(() => import('./pages/SystemReport'));
const MobileDeviceManagementPage = lazy(() => import('./pages/MobileDeviceManagementPage'));
const OperatorsAdminPage = lazy(() => import('./pages/OperatorsAdminPage'));
const BillingTariffsAdminPage = lazy(() => import('./pages/BillingTariffsAdminPage'));

// --- Admin Bundle ---
const StammdatenPage = lazy(() => import('./pages/admins/stammdatenPage'));
const StationPage = lazy(() => import('./pages/admins/stationPage'));
const VehiclePage = lazy(() => import('./pages/admins/vehiclesPage'));
const ShiftPlanPage = lazy(() => import('./pages/admins/shiftPlanPage'));
const ShiftPlanPageMulti = lazy(() => import('./pages/admins/shiftPlanPageMulti'));
const Flottenverwaltung = lazy(() => import('./pages/admins/FlottenverwaltungList'));
const ImportExportPage = lazy(() => import('./pages/admins/ImportExportPage'));
const SetupPage = lazy(() => import('./pages/admins/SetupPage'));
const PlanningScoreConfigPage = lazy(() => import('./pages/admins/PlanningScoreConfigPage'));
const VehicleCategoriesPage = lazy(() => import('./pages/admins/VehicleCategoriesPage'));

// --- Statistics Bundle ---
const StatisticsDashboard = lazy(() => import('./pages/statistics/StatisticsDashboard'));

// --- Planning Bundle ---
const PlanningPage = lazy(() => import('./pages/planningDetails'));
const VehicleTimeline = lazy(() => import('./pages/planning/VehicleTimelinePage'));

// --- Map Bundle ---
const VehicleMapPage = lazy(() => import('./pages/VehicleMapPage'));

// --- Heavy Single Components ---
const AdminBenchmarkPage = lazy(() => import('./pages/AdminBenchmarkPage'));
const AdminCodiPage = lazy(() => import('./pages/AdminCodiPage'));
const AdminCommunicatorPage = lazy(() => import('./pages/AdminCommunicatorPage'));
const OrToolsAdminPage = lazy(() => import('./pages/admin/OrToolsAdminPage'));
const AdminMobileSimulationPage = lazy(() => import('./pages/AdminMobileSimulationPage'));
const AdminSystemReportPage = lazy(() => import('./pages/AdminSystemReportPage'));
const BugReportsAdminPage = lazy(() => import('./pages/BugReportsAdminPage'));
const TransportArchivePage = lazy(() => import('./pages/archive/TransportArchivePage'));
const AdminDevToolsPage = lazy(() => import('./pages/AdminDevToolsPage'));

// Home-Komponente mit Weiterleitung
const Home = () => {
  return <Navigate to="/login" replace />;
};

// Wrapper für ErrorBoundary + PrivateRoute + Suspense (für lazy-loaded Komponenten)
const ProtectedRoute = ({ children, adminOnly, requiredFeature }) => (
  <ErrorBoundary>
    <PrivateRoute adminOnly={adminOnly} requiredFeature={requiredFeature}>
      <MainLayout>
        <Suspense fallback={<LazyLoadFallback />}>
          {children}
        </Suspense>
      </MainLayout>
    </PrivateRoute>
  </ErrorBoundary>
);

// Main App component
function App() {
  // Router-Konfiguration erstellen
  const router = createBrowserRouter([
    {
      path: "/",
      element: <Home />,
      errorElement: <ErrorPage />
    },
    {
      path: "/registration",
      element: <MainLayout><RegistrationPage /></MainLayout>,
      errorElement: <ErrorPage />
    },
    {
      path: "/confirmation",
      element: <MainLayout><ConfirmationPage /></MainLayout>,
      errorElement: <ErrorPage />
    },
    {
      path: "/reset-password",
      element: <MainLayout><PasswordResetPage /></MainLayout>,
      errorElement: <ErrorPage />
    },
    {
      path: "/login",
      element: <Login />,
      errorElement: <ErrorPage />
    },
    {
      path: "/2fa-setup",
      element: <ForcedTwoFactorSetupPage />,
      errorElement: <ErrorPage />
    },
    {
      path: "/dashboard",
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    // Admin Route
    {
      path: "/admin",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-DASHBOARD">
          <AdminNavigatorPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/userspage",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BENUTZERVERWALTUNG">
          <UsersList />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/role-management",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-ROLLEN-UND-RECHTE">
          <RoleManagementPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/transportlist",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <TransportList />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/transport",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <TransportFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/transportseries",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <HeadOfSeriesList />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/transport/:id/:copy?/:view?",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <TransportFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/krankenfahrt",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <KrankenfahrtFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/krankenfahrt/:id/:copy?/:view?",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <KrankenfahrtFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/krankenfahrt/:id",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <KrankenfahrtFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/medical-material",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <MedicalMaterialFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/medical-material/:id/:copy?/:view?",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <MedicalMaterialFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/medical-material/:id",
      element: (
        <ProtectedRoute requiredFeature="BESTELLUNGEN">
          <MedicalMaterialFormPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/institutions",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <InstitutionsList />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/institutionpage",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <InstitutionPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/institutionpage/:institution_id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <InstitutionPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/logs",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-LOGFILES">
          <LogsPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/timeSlotManagement",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <TimeSlotManagement />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/infections",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <InfektionenList />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/mobile-app-master-data",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <MobileAppMasterData />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/params",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-PARAMETER">
          <ConfigTabs />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/planning-score-config",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-PARAMETER">
          <PlanningScoreConfigPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/edituser",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BENUTZERVERWALTUNG">
          <EditUserPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/edituser/:username",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BENUTZERVERWALTUNG">
          <EditUserPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/changepassword",
      element: (
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/settings",
      element: (
        <ProtectedRoute>
          <UserSettingsPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/trusted-devices",
      element: (
        <ProtectedRoute>
          <TrustedDevicesPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/impressum",
      element: (
        <ProtectedRoute>
          <Impressum />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/datenschutz",
      element: (
        <ProtectedRoute>
          <Datenschutz />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/agb",
      element: (
        <MainLayout>
          <AGB />
        </MainLayout>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/releasenotes",
      element: (
        <ProtectedRoute>
          <ReleaseNotes />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/test-error",
      element: (
        <ProtectedRoute>
          <ErrorComponent />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/stammdaten",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <StammdatenPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/stationpage",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <StationPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/stationpage/:station_id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <StationPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/vehiclepage",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <VehiclePage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/vehiclepage/:vehicle_id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <VehiclePage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/vehicle-categories",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <VehicleCategoriesPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/shiftplanpage",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <ShiftPlanPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/shiftplanpage/:id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <ShiftPlanPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/shiftplanpage-multi",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <ShiftPlanPageMulti />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/shiftplanpage-multi/:id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-STAMMDATEN">
          <ShiftPlanPageMulti />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/flottenverwaltung",
      element: (
        <ProtectedRoute adminOnly requiredFeature="FLOTTENVERWALTUNG">
          <Flottenverwaltung />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/importexport",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-IMPORT-EXPORT">
          <ImportExportPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/setuppage",
      element: (
        <ProtectedRoute adminOnly>
          <SetupPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/planning/:planning_id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="GRAFISCHE-PLANUNG">
          <PlanningPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/planning/transport/:transport_id",
      element: (
        <ProtectedRoute adminOnly requiredFeature="GRAFISCHE-PLANUNG">
          <PlanningPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/timeline",
      element: (
        <ProtectedRoute adminOnly requiredFeature="GRAFISCHE-PLANUNG">
          <VehicleTimeline />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/vehicles/map",
      element: (
        <ProtectedRoute adminOnly requiredFeature="KARTENANSICHT-FAHRZEUGE">
          <VehicleMapPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/mail-templates",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-MAIL-TEMPLATES">
          <MailTemplatesPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/bug-reports",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BUG-VERWALTUNG">
          <BugReportsAdminPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/notification-preferences",
      element: (
        <ProtectedRoute>
          <NotificationPreferencesPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/notifications",
      element: (
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/admin-communicator",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-KOMMUNIKATOR">
          <AdminCommunicatorPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/admin-codi",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-CODI">
          <AdminCodiPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/benchmark",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BENCHMARK-TESTS">
          <AdminBenchmarkPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/transport-archive",
      element: (
        <ProtectedRoute requiredFeature="TRANSPORT-ARCHIVE">
          <TransportArchivePage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/statistics",
      element: (
        <ProtectedRoute requiredFeature="STATISTICS-DASHBOARD">
          <StatisticsDashboard />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/mobile-api-simulation",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-MOBILE-API-SIMALTION">
          <AdminMobileSimulationPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/system-report",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SYSTEM-REPORT">
          <AdminSystemReportPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/or-tools-admin",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ORTOOLS-ADMIN">
          <OrToolsAdminPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/proxy-monitoring",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-PARAMETER">
          <SystemReport />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/mobile-device-management",
      element: (
        <ProtectedRoute adminOnly requiredFeature="MOBILE-DEVICE-MANAGEMENT">
          <MobileDeviceManagementPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/operators",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-BETREIBER-VERWALTUNG">
          <OperatorsAdminPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/billing-tariffs",
      element: (
        <ProtectedRoute requiredFeature="ADMIN-ABRECHNUNG-TARIFE">
          <BillingTariffsAdminPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/tenant-agb/:tenantId",
      element: (
        <MainLayout>
          <TenantAGBView />
        </MainLayout>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/agb-management",
      element: (
        <ProtectedRoute adminOnly requiredFeature="ADMIN-SONSTIGES">
          <TenantAGBManagement />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: "/dev-tools",
      element: (
        <ProtectedRoute adminOnly>
          <AdminDevToolsPage />
        </ProtectedRoute>
      ),
      errorElement: <ErrorPage />
    },
  ]);

  return (
    <ErrorBoundary>
      <UserProvider>
        <ThemeContextProvider>
          <CssBaseline />
          <ConfigProvider>
            <PermissionProvider>
              <VehicleTypesProvider>
                <Suspense fallback={<LazyLoadFallback />}>
                  <RouterProvider router={router} />
                </Suspense>
              </VehicleTypesProvider>
            </PermissionProvider>
          </ConfigProvider>
        </ThemeContextProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;
